import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { lotteryApi } from '../../api/lottery.js';
import { ErrorBox } from '../../components/ui.js';
import { ApiError } from '../../api/client.js';

/**
 * 송장입력 — 원본 lottery/order/delivery_excel_check.php + _exec.php.
 *
 * 택배사에서 받은 엑셀을 브라우저에서 읽어(SheetJS), 아직 안 나간 주문(미배송)의
 * 연락처와 대조한다. 일치하는 건만 '배송완료' + 송장번호로 반영한다.
 *
 * 원본과 같은 열 위치를 쓴다 — 원본 화면 안내문 그대로 **V열(22번째, index 21)이
 * 고객전화번호, H열(8번째, index 7)이 운송장번호**다. randombox 쪽 송장입력은
 * 열 위치가 다르므로(9번째·22번째) 두 화면을 섞어 쓰면 안 된다.
 *
 * 원본은 랜덤박스(kuji)를 안 고르면 아예 "랜덤박스가 지정되지 않았습니다" 로
 * 막았다. 여기서도 같게 막는다 — 회차를 안 좁히면 같은 번호를 쓰는 다른 회차
 * 주문까지 배송완료로 바뀔 수 있다.
 */

/** 원본 delivery_excel_check.php 의 비교 규칙(대시 제거)과 같다. */
function normalizePhone(raw: string): string {
  return raw.replace(/-/g, '').replace(/\s/g, '').replace(/\|C\|I$/, '');
}

interface ParsedRow {
  phone: string;
  tracking: string;
  matched: boolean;
}

export function TrackingPage() {
  const [params] = useSearchParams();
  const kuji = params.get('kuji') ?? '';

  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [dragging, setDragging] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const pending = useQuery({
    queryKey: ['lottery', 'tracking', 'pending', kuji],
    queryFn: () => lotteryApi.trackingPending(kuji === '' ? undefined : Number(kuji)),
    enabled: false,
  });

  const apply = useMutation({
    mutationFn: (updates: { hphone: string; trackingNumber: string }[]) =>
      lotteryApi.applyTracking({ kuji: kuji === '' ? undefined : Number(kuji), updates }),
  });

  const matchedCount = useMemo(() => rows.filter((r) => r.matched).length, [rows]);

  function reset() {
    setFileName('');
    setRows([]);
    setParseError(null);
    apply.reset();
    if (fileInput.current) fileInput.current.value = '';
  }

  async function processFile(file: File) {
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setParseError('엑셀 파일(.xlsx, .xls)만 업로드할 수 있습니다.');
      return;
    }
    setParseError(null);
    setFileName(file.name);

    try {
      const pendingResult = await pending.refetch();
      const dbRows = pendingResult.data ?? [];

      // xlsx(SheetJS)는 무거워서 이 화면에 들어올 때만 받도록 동적 import 로 뺐다.
      const { read, utils } = await import('xlsx');
      const buf = await file.arrayBuffer();
      const workbook = read(buf, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]!];
      const table = utils.sheet_to_json<unknown[]>(sheet!, { header: 1, raw: false });

      const parsed: ParsedRow[] = [];
      // 원본과 같이 첫 줄(헤더)은 건너뛴다.
      table.forEach((line, idx) => {
        if (idx === 0 || !Array.isArray(line)) return;
        const phone = line[21] != null ? String(line[21]).trim() : '';
        const tracking = line[7] != null ? String(line[7]).trim() : '';
        if (!phone || !tracking) return;

        const normalized = normalizePhone(phone);
        const matched = dbRows.some((db) => normalizePhone(db.hphone) === normalized);
        parsed.push({ phone, tracking, matched });
      });

      setRows(parsed);
      if (parsed.length === 0) {
        setParseError('유효한 데이터가 없습니다. V열(고객전화번호)·H열(운송장번호)을 확인해주세요.');
      }
    } catch {
      setRows([]);
      setParseError('데이터 조회 및 엑셀 파싱 중 오류가 발생했습니다.');
    }
  }

  function submit() {
    const updates = rows
      .filter((r) => r.matched)
      .map((r) => ({ hphone: r.phone, trackingNumber: r.tracking }));
    if (updates.length === 0) return;
    apply.mutate(updates);
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <Link className="link link--back" to="/lottery/orders">
            ← F주문관리
          </Link>
          <h1 className="page-title">송장입력</h1>
          <p className="page-desc">
            택배사 엑셀을 올리면 <strong>미배송</strong> 주문과 연락처로 대조합니다. 일치하는 건만{' '}
            <strong>배송완료</strong>로 바뀌고 송장번호가 들어갑니다. 형식: <code>V열</code>=고객전화번호,{' '}
            <code>H열</code>=운송장번호.
          </p>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--ghost btn--sm" onClick={reset}>
            초기화
          </button>
        </div>
      </header>

      {kuji === '' ? (
        // 원본 delivery_excel_check.php:10 - 랜덤박스가 없으면 아예 열리지 않았다.
        <p className="hint hint--warn">
          랜덤박스를 먼저 골라주세요. <Link className="link" to="/lottery/orders">F주문관리</Link> 에서
          랜덤박스를 고른 뒤 <strong>송장입력</strong> 을 눌러야 그 회차만 안전하게 처리됩니다.
        </p>
      ) : (
        <>
          <div
            className={dragging ? 'drop-zone drop-zone--active' : 'drop-zone'}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer.files[0];
              if (f) void processFile(f);
            }}
            onClick={() => fileInput.current?.click()}
          >
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void processFile(f);
              }}
            />
            <span className="drop-zone__msg">
              {fileName ? fileName : '엑셀 파일을 이곳에 드래그하거나 클릭하세요.'}
            </span>
          </div>

          {parseError && <ErrorBox error={new Error(parseError)} />}
          {apply.isError && (
            <ErrorBox
              error={apply.error instanceof ApiError ? apply.error : new Error('처리에 실패했습니다.')}
            />
          )}
          {apply.isSuccess && (
            <p className="ok-note">
              {apply.data.updatedCount}건 처리가 완료되었습니다 (요청 {apply.data.totalUpdates}건).
            </p>
          )}

          {rows.length > 0 && (
            <>
              <p className="hint">
                일치 <strong style={{ color: 'var(--ok)' }}>{matchedCount}건</strong> / 불일치{' '}
                <strong style={{ color: 'var(--danger)' }}>{rows.length - matchedCount}건</strong>
              </p>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>고객전화번호(V열)</th>
                      <th>운송장번호(H열)</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{r.phone}</td>
                        <td><code className="code">{r.tracking}</code></td>
                        <td>
                          {r.matched ? (
                            <span className="badge">매칭됨</span>
                          ) : (
                            <span className="badge badge--danger">매칭안됨</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {matchedCount > 0 && (
                <button
                  type="button"
                  className="btn btn--primary btn--block"
                  style={{ marginTop: 14 }}
                  disabled={apply.isPending}
                  onClick={submit}
                >
                  {apply.isPending ? '처리 중…' : `${matchedCount}건 송장번호 반영`}
                </button>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
